import { useEffect, useRef, useState } from 'react'
import {
  chartRows,
  chartScale,
  daysOutLabel,
  pointTitle,
  seriesPath,
  type ChartData,
  type ChartScale,
} from './registration-chart-data'

type PlotProps = { data: ChartData; scale: ChartScale; width: number }

export function RegistrationChartPlot({ data }: { data: ChartData }) {
  const container = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(1040)
  useEffect(() => {
    const element = container.current!
    const measure = () => setWidth(element.clientWidth || 1040)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const scale = chartScale(data, width)
  return (
    <div ref={container}>
      <svg
        role="img"
        aria-label="Places held by days before the event"
        className="block w-full"
        height="200"
        viewBox={`0 0 ${width} 200`}
        preserveAspectRatio="none"
      >
        <Axes data={data} scale={scale} width={width} />
        <Series data={data} scale={scale} />
        <Today data={data} scale={scale} width={width} />
        <PointTitles data={data} scale={scale} />
      </svg>
    </div>
  )
}

function Axes({ scale, width }: PlotProps) {
  return (
    <>
      {scale.ticks.map((tick) => (
        <g key={tick}>
          <line
            className="stroke-border"
            x1={44}
            x2={width - 16}
            y1={scale.y(tick)}
            y2={scale.y(tick)}
          />
          <text
            className="fill-muted-foreground text-caption tabular-nums"
            textAnchor="end"
            x={36}
            y={scale.y(tick) + 4}
          >
            {tick}
          </text>
        </g>
      ))}
      {scale.days.map((day, index) => (
        <text
          key={day}
          className="fill-muted-foreground text-caption tabular-nums"
          textAnchor={tickAnchor(index, scale.days.length)}
          x={scale.x(day)}
          y={178}
        >
          {daysOutLabel(day)}
        </text>
      ))}
      <text
        className="fill-muted-foreground text-caption"
        textAnchor="middle"
        x={(44 + width - 16) / 2}
        y={196}
      >
        Days before event starts
      </text>
    </>
  )
}

function tickAnchor(index: number, length: number) {
  if (index === 0) return 'start'
  return index === length - 1 ? 'end' : 'middle'
}

function Series({ data, scale }: Omit<PlotProps, 'width'>) {
  return (
    <>
      {data.previous && data.previous.length > 0 && (
        <path
          aria-label={`${data.previousLabel} places held`}
          d={seriesPath(data.previous, scale)}
          className="stroke-muted-foreground"
          fill="none"
          opacity={0.85}
          strokeDasharray="4 3"
          strokeWidth={1.25}
        />
      )}
      {data.current.length > 0 && (
        <path
          aria-label={`${data.currentLabel} places held`}
          d={seriesPath(data.current, scale)}
          className="stroke-primary"
          fill="none"
          strokeWidth={2}
        />
      )}
    </>
  )
}

function Today({ data, scale, width }: PlotProps) {
  const current = data.current.find((point) => point.day === data.todayDay)
  if (!current) return null
  const previous = data.previous?.find((point) => point.day === data.todayDay)
  const x = scale.x(current.day)
  const nearLeft = x < width / 3
  return (
    <g>
      <line className="stroke-border" x1={x} x2={x} y1={12} y2={160} />
      {previous && (
        <circle
          className="fill-card stroke-muted-foreground"
          cx={x}
          cy={scale.y(previous.value)}
          r={3}
          strokeWidth={1.5}
        />
      )}
      <circle className="fill-primary" cx={x} cy={scale.y(current.value)} r={3.5} />
      <text
        className="fill-foreground text-caption"
        textAnchor={nearLeft ? 'start' : 'end'}
        x={x + (nearLeft ? 6 : -6)}
        y={22}
      >
        Today
      </text>
    </g>
  )
}

function PointTitles({ data, scale }: Omit<PlotProps, 'width'>) {
  const rows = chartRows(data)
  return rows.map((row, index) => {
    const x = scale.x(row.day)
    const before = scale.x(rows[index - 1]?.day ?? row.day)
    const after = scale.x(rows[index + 1]?.day ?? row.day)
    return (
      <rect
        key={row.day}
        fill="transparent"
        height={148}
        width={Math.max(1, (after - before) / 2)}
        x={(before + x) / 2}
        y={12}
      >
        <title>{pointTitle(row, data)}</title>
      </rect>
    )
  })
}
